<template>
  <div class="vs-panel">
    <div class="vs-header">
      <h4 class="vs-title">Video Shorts <span v-if="shorts.length" class="vs-count">({{ shorts.length }})</span></h4>
      <span v-if="shorts.length" class="vs-meta-line">9:16 portrait · one per gold quote</span>
    </div>

    <div v-if="!shorts.length" class="vs-empty">
      <span>No video shorts generated yet.</span>
    </div>

    <div v-else class="vs-list">
      <div v-for="(reel, i) in shorts" :key="reel.id || ('vs-' + i)" class="vs-row">
        <div class="vs-row-head">
          <span class="vs-row-index">vs{{ i }}</span>
          <span class="vs-row-title">{{ reel.title || '(untitled short)' }}</span>
          <span class="vs-row-duration">{{ formatDuration(reel.duration_ms) }}</span>
        </div>

        <div v-if="goldQuoteFor(reel)" class="vs-row-quote">
          <span class="vs-section-label">Gold quote #{{ reel.gold_quote_ref }}</span>
          <blockquote>{{ goldQuoteFor(reel) }}</blockquote>
        </div>

        <div class="vs-row-audio">
          <span class="vs-section-label">Audio</span>
          <span v-if="reel.audio_ref" class="vs-audio-set">
            <span class="vs-audio-badge is-set">Set</span>
            <code class="vs-audio-url">{{ reel.audio_ref }}</code>
          </span>
          <span v-else class="vs-audio-set">
            <span class="vs-audio-badge is-empty">Not set</span>
            <span class="vs-audio-hint">Audio ref empty — creator-controlled. Default: <code>/audio/{collection_slug}/shorts.mp3</code></span>
          </span>
        </div>

        <div v-if="reel.bundle_html" class="vs-row-preview">
          <div class="vs-preview-header">
            <span class="vs-section-label">Bundle preview (9:16)</span>
            <el-button size="mini" type="text" :loading="loadingId === (reel.id || ('vs-' + i))" @click="togglePreview(reel, i)">
              {{ activeId === (reel.id || ('vs-' + i)) ? 'Hide' : 'Show' }}
            </el-button>
          </div>
          <div v-if="activeId === (reel.id || ('vs-' + i))" class="vs-preview-frame">
            <iframe
              :src="frameSrcs[reel.id || ('vs-' + i)] || ''"
              sandbox="allow-scripts allow-same-origin"
              referrerpolicy="no-referrer"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoShortsPanel",
  props: {
    collectionForm: { type: Object, required: true },
    collectionId: { type: String, default: "" },
  },
  data() {
    return {
      activeId: null,
      loadingId: null,
      frameSrcs: {},      // map: reel_key -> Blob URL
    };
  },
  computed: {
    shorts() {
      return (this.collectionForm && Array.isArray(this.collectionForm.video_shorts))
        ? this.collectionForm.video_shorts
        : [];
    },
    goldQuotes() {
      return (this.collectionForm && Array.isArray(this.collectionForm.gold_quotes))
        ? this.collectionForm.gold_quotes
        : [];
    },
  },
  methods: {
    formatDuration(ms) {
      if (!ms) return "—";
      const sec = Math.round(ms / 1000);
      return sec + "s";
    },
    goldQuoteFor(reel) {
      const idx = reel && typeof reel.gold_quote_ref === "number" ? reel.gold_quote_ref : -1;
      const q = this.goldQuotes[idx];
      return q && q.insight ? q.insight : "";
    },
    togglePreview(reel, i) {
      const key = reel.id || ("vs-" + i);
      if (this.activeId === key) {
        this.activeId = null;
        return;
      }
      // Lazy-build Blob URL on first open
      if (!this.frameSrcs[key] && reel.bundle_html) {
        this.loadingId = key;
        try {
          const blob = new Blob([reel.bundle_html], { type: "text/html" });
          const url = URL.createObjectURL(blob);
          this.$set(this.frameSrcs, key, url);
        } finally {
          this.loadingId = null;
        }
      }
      this.activeId = key;
    },
  },
  beforeDestroy() {
    // Revoke any Blob URLs we created so they don't leak.
    Object.values(this.frameSrcs).forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (e) { /* noop */ }
    });
  },
};
</script>

<style scoped>
.vs-panel { padding: 4px 0 8px; }
.vs-header { display: flex; align-items: baseline; gap: 12px; margin-bottom: 12px; }
.vs-title { margin: 0; font-size: 16px; font-weight: 600; color: #303133; }
.vs-count { color: #909399; font-weight: normal; }
.vs-meta-line { font-size: 12px; color: #909399; font-style: italic; }
.vs-empty { padding: 22px; text-align: center; color: #909399; font-style: italic; background: #fafafa; border-radius: 4px; }
.vs-list { display: flex; flex-direction: column; gap: 14px; }
.vs-row { padding: 14px 16px; border: 1px solid #e4e7ed; border-radius: 6px; background: #fff; }
.vs-row-head { display: flex; align-items: baseline; gap: 12px; margin-bottom: 10px; }
.vs-row-index { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #fff; background: #909399; padding: 2px 8px; border-radius: 3px; letter-spacing: 0.04em; }
.vs-row-title { font-size: 15px; font-weight: 600; color: #303133; flex: 1; min-width: 0; }
.vs-row-duration { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #606266; }
.vs-section-label { font-size: 11px; color: #909399; text-transform: uppercase; letter-spacing: 0.05em; display: inline-block; margin-right: 8px; }
.vs-row-quote { padding: 6px 0; }
.vs-row-quote blockquote { margin: 4px 0 0; padding: 8px 14px; border-left: 3px solid #409eff; background: #f7faff; color: #303133; font-style: italic; font-size: 13px; }
.vs-row-audio { padding: 6px 0; display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.vs-audio-set { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.vs-audio-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; }
.vs-audio-badge.is-set { background: #f0f9eb; color: #67c23a; }
.vs-audio-badge.is-empty { background: #f4f4f5; color: #909399; }
.vs-audio-url { font-family: SFMono-Regular, Menlo, monospace; font-size: 12px; color: #303133; background: #f4f4f5; padding: 2px 6px; border-radius: 3px; }
.vs-audio-hint { font-size: 12px; color: #909399; }
.vs-audio-hint code { font-family: SFMono-Regular, Menlo, monospace; font-size: 11px; color: #606266; background: #f4f4f5; padding: 1px 4px; border-radius: 2px; }
.vs-row-preview { padding: 6px 0; }
.vs-preview-header { display: flex; align-items: baseline; gap: 12px; }
.vs-preview-frame { margin-top: 8px; width: 270px; height: 480px; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; background: #000; }
.vs-preview-frame iframe { width: 100%; height: 100%; border: 0; }
</style>
